import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import DataTable, { Pill, formatUGX } from "components/DataTable/DataTable.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  searchBar: { display: "flex", gap: "12px", marginBottom: "16px", alignItems: "flex-end", flexWrap: "wrap" },
  infoText: { color: "#666", fontSize: "13px", marginBottom: "16px" }
};

class Suppliers extends Component {
  state = {
    orders: [],
    loading: true,
    search: "",
    error: null
  };

  componentDidMount() {
    this.fetchOrders();
  }

  fetchOrders = async () => {
    this.setState({ loading: true });
    try {
      const res = await api.get("/procurement", { params: { limit: 500 } });
      const d = res.data.data || res.data;
      this.setState({ orders: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) {
      this.setState({ error: "Could not load procurement data.", loading: false });
    }
  };

  deriveSuppliers = (orders) => {
    const map = {};
    orders.forEach(o => {
      const name = (o.supplier || "").trim();
      if (!name) return;
      if (!map[name]) {
        map[name] = {
          name,
          orderCount: 0,
          totalValue: 0,
          statuses: [],
          lastOrderDate: null,
          lastOrderTitle: ""
        };
      }
      map[name].orderCount += 1;
      map[name].totalValue += o.totalAmount ? Number(o.totalAmount) : 0;
      if (o.status) map[name].statuses.push(o.status);
      if (o.createdAt) {
        if (!map[name].lastOrderDate || o.createdAt > map[name].lastOrderDate) {
          map[name].lastOrderDate = o.createdAt;
          map[name].lastOrderTitle = o.title || "—";
        }
      }
    });
    return Object.values(map).sort((a, b) => b.orderCount - a.orderCount);
  };

  render() {
    const { classes } = this.props;
    const { orders, loading, search, error } = this.state;

    const suppliers = this.deriveSuppliers(orders);

    const filtered = search.trim()
      ? suppliers.filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
      : suppliers;

    const columns = [
      { id: "name", header: "Supplier Name", accessorFn: s => s.name },
      { id: "orderCount", header: "Total Orders", accessorFn: s => s.orderCount },
      {
        id: "totalValue",
        header: "Total Value (UGX)",
        accessorFn: s => s.totalValue,
        cell: info => formatUGX(info.getValue()),
        meta: { align: "right" }
      },
      {
        id: "summary",
        header: "Order Summary",
        cell: info => {
          const s = info.row.original;
          const delivered = s.statuses.filter(st => st === "delivered").length;
          const pending = s.statuses.filter(st => ["draft", "approved", "ordered"].includes(st)).length;
          return (
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              {delivered > 0 && <Pill tone="success">{delivered} delivered</Pill>}
              {pending > 0 && <Pill tone="warning">{pending} pending</Pill>}
              {delivered === 0 && pending === 0 && <Pill tone="muted">no active</Pill>}
            </div>
          );
        }
      },
      { id: "lastOrderDate", header: "Last Order Date", accessorFn: s => (s.lastOrderDate ? s.lastOrderDate.substring(0, 10) : "—") },
      { id: "lastOrder", header: "Last Order", accessorFn: s => s.lastOrderTitle || "—" }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Suppliers</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    Derived from purchase orders · {filtered.length} supplier{filtered.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <Button size="sm" color="white" onClick={this.fetchOrders}>
                    <Icon style={{ fontSize: "16px", marginRight: "4px" }}>refresh</Icon> Refresh
                  </Button>
                </div>
            </CardHeader>
            <CardBody>
              <ViewToolbar tabs={[]} actions={[]} />
              <p className={classes.infoText}>
                Supplier information is automatically extracted from purchase orders. To add a new supplier, create a purchase order referencing them.
              </p>

              <div className={classes.searchBar}>
                <input
                  type="text"
                  placeholder="Search suppliers..."
                  value={search}
                  onChange={e => this.setState({ search: e.target.value })}
                  style={{ flex: 1, maxWidth: "300px", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" }}
                />
              </div>

              {error && <p style={{ color: "#f44336" }}>{error}</p>}

              <DataTable
                columns={columns}
                data={filtered}
                isLoading={loading}
                emptyState={search ? "No suppliers match your search." : "No supplier data available. Create purchase orders to see suppliers here."}
              />
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }
}

Suppliers.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(Suppliers));
